import React, { useState } from 'react';
import { PhoneFrame } from './components/PhoneFrame';
import { ChatDock } from './components/ChatDock';
import { ChatSheet } from './components/ChatSheet';
import { BottomModal } from './components/BottomModal';
import { WeatherModal } from './components/modals/WeatherModal';
import { TrackingModal } from './components/modals/TrackingModal';
import { JourneyModal } from './components/modals/JourneyModal';
import { CarePlansModal, PlanView } from './components/modals/CarePlansModal';
import { RelaxModal, RelaxView, RelaxStart } from './components/modals/RelaxModal';
import { VoiceActivity } from './components/VoiceBar';
import { Dashboard } from './screens/Dashboard';
import { LibraryScreen } from './screens/LibraryScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { NhsScreen } from './screens/NhsScreen';
import { HealthBuddy, BuddyMood } from './components/HealthBuddy';
import { ModalKind } from './data/mock';
import { useConditions } from './data/conditions';
import { JourneyScreen } from './screens/JourneyScreen';
import { FullScreenModal } from './components/FullScreenModal';
type Screen = 'dashboard';
/**
 * dhp.voice — app mockup, core screens.
 *
 * Everything inside the phone frame is the app. The panel on the left is the
 * mockup's own presentation layer: a numbered inventory of screens and flows
 * that drives the prototype (and doubles as the screen inventory for the
 * handover reference doc).
 */
export function App() {
  const {
    selectedConditions
  } = useConditions();
  const [screen, setScreen] = useState<Screen>('dashboard');
  const [chatOpen, setChatOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const [pendingScript, setPendingScript] = useState<string | null>(null);
  // conversational entry skips the tracker picker (semantic routing) —
  // a tracker id opens that tracker's form, 'history' opens the history view
  const [trackingStart, setTrackingStart] = useState<string | null>(null);
  // conversational entry skips the journey map — straight to the current lesson
  const [journeyStart, setJourneyStart] = useState<'map' | 'lesson'>('map');
  // conversational entry skips the plan picker — "I'm struggling to breathe"
  // goes straight to the emergency steps, no intermediary, no confirmation
  const [plansStart, setPlansStart] = useState<PlanView>('picker');
  // which care-plans view is showing (the emergency view calms the buddy)
  const [plansView, setPlansView] = useState<PlanView>('picker');
  // conversational entry skips the session picker — "I can't sleep" goes
  // straight to the wind-down player, already playing
  const [relaxStart, setRelaxStart] = useState<RelaxStart>('picker');
  // which relax view is showing (the player view calms the buddy)
  const [relaxView, setRelaxView] = useState<RelaxView>('picker');
  // voice mode — toggled by the Sunny avatar in the modal header
  // (journey + care plans + relax share the one preference)
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceActivity, setVoiceActivity] = useState<VoiceActivity>('idle');
  // Journey is a full-screen slide-in (default nav); the bottom modal is only
  // the lesson "start" experience.
  const [journeyOpen, setJourneyOpen] = useState(false);
  // Library is a full-screen modal flow (list → article in one stack)
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryStart, setLibraryStart] = useState<'list' | 'article'>('list');
  // Settings is a full-screen modal flow (hub → sub-views in one stack)
  const [settingsOpen, setSettingsOpen] = useState(false);
  // NHS is a full-screen modal flow
  const [nhsOpen, setNhsOpen] = useState(false);
  // when a plan deep-link keeps the picker in the back stack (back arrow)
  const [plansAllowBack, setPlansAllowBack] = useState(false);
  /* ---- interactions ---- */
  const openModal = (m: ModalKind, start?: string) => {
    if (m === 'tracking') setTrackingStart(null); // touch entry → picker
    if (m === 'journey') {
      // touch entry → the full-screen Journey (conditions list → map)
      setJourneyStart('map');
      setJourneyOpen(true);
      return;
    }
    if (m === 'article') {
      // article opens inside the Library stack (no bottom modal)
      setLibraryStart('article');
      setLibraryOpen(true);
      return;
    }
    if (m === 'plans') {
      // a deep-link (e.g. the Action Plan favourite, Emergency Plan support)
      // opens straight to that view but keeps the picker in the back stack;
      // a plain tap opens the picker
      const view = start as PlanView ?? 'picker';
      setPlansStart(view);
      setPlansView(view);
      setPlansAllowBack(start != null && start !== 'picker');
    }
    if (m === 'relax') {
      // touch entry → the session picker (Tonight + Last night zones)
      setRelaxStart('picker');
      setRelaxView('picker');
    }
    setModal(m);
  };
  const openModalFromChat = (m: ModalKind, start?: string) => {
    // article opens inside the Library stack (no bottom modal)
    if (m === 'article') {
      setLibraryStart('article');
      setLibraryOpen(true);
      return;
    }
    // tracking routes semantically: "log that I used my inhaler" → straight
    // to the inhaler form, "how's my tracking going?" → the history view
    if (m === 'tracking') setTrackingStart(start ?? 'asthma-medication');
    // "resume my journey" → straight to the current step, skipping the map
    if (m === 'journey') setJourneyStart('lesson');
    // care plans route semantically: "show my care plan" → action plan,
    // "when do I take my preventer?" → med schedule,
    // "I'm struggling to breathe" → emergency steps, top priority
    if (m === 'plans') {
      const view = start as PlanView ?? 'action-plan';
      setPlansStart(view);
      setPlansView(view);
      setPlansAllowBack(false); // chat answers are direct — no picker back-stack
      // emergency by voice/chat → Sunny reads the steps aloud automatically;
      // the user asked by speaking, so the answer comes back spoken
      if (view === 'emergency') setVoiceMode(true);
    }
    // relax & sleep routes semantically: "I can't sleep" → wind-down player
    // already playing, "log my sleep" → sleep log form, "play the rain
    // sounds one" → player with the rain soundscape pre-selected
    if (m === 'relax') {
      const target = start as RelaxStart ?? 'wind-down';
      setRelaxStart(target);
      setRelaxView(target === 'sleep-log' ? 'sleep-log' : 'player');
    }
    setModal(m);
  };
  const closeModal = () => {
    setModal(null);
    setTrackingStart(null);
    setJourneyStart('map');
    setPlansStart('picker');
    setPlansView('picker');
    setRelaxStart('picker');
    setRelaxView('picker');
    setVoiceActivity('idle');
  };
  /* med schedule "log this" → the Tracking inhaler form (the cross-flow:
     Care Plans is the plan, Tracking is the record) */
  const handleLogMed = () => {
    setModal(null);
    setPlansStart('picker');
    setPlansView('picker');
    setTrackingStart('asthma-medication');
    setTimeout(() => setModal('tracking'), 250);
  };
  const handleFeature = (id: string) => {
    if (id === 'library') {
      setLibraryStart('list');
      setLibraryOpen(true);
    }
    if (id === 'nhs') setNhsOpen(true);
    if (id === 'weather') setModal('weather');
    if (id === 'tracking') openModal('tracking');
    if (id === 'plans') openModal('plans');
    if (id === 'journey') openModal('journey');
    if (id === 'relax') openModal('relax');
  };
  const handleDockSuggestion = (scriptId: string) => {
    setPendingScript(scriptId);
    setChatOpen(true);
  };
  const handleDockMic = () => {
    setPendingScript('pollen');
    setChatOpen(true);
  };
  const reset = () => {
    setScreen('dashboard');
    setChatOpen(false);
    setModal(null);
    setJourneyOpen(false);
    setLibraryOpen(false);
    setSettingsOpen(false);
    setNhsOpen(false);
    setTrackingStart(null);
    setJourneyStart('map');
    setPlansStart('picker');
    setPlansView('picker');
    setRelaxStart('picker');
    setRelaxView('picker');
    setVoiceActivity('idle');
  };
  /* the modal header buddy mirrors voice state: mouths the audio while
     Sunny reads aloud, ripples while listening, idle when voice is off */
  const voiceBuddyMood: BuddyMood = !voiceMode ? 'idle' : voiceActivity === 'speaking' ? 'speaking' : voiceActivity === 'listening' ? 'listening' : 'idle';
  const journeyBuddyMood: BuddyMood = voiceBuddyMood;
  /* in the emergency view the buddy stays calm and steady (unless it's
     actively reading/listening in voice mode) */
  const plansBuddyMood: BuddyMood = plansView === 'emergency' && voiceBuddyMood === 'idle' ? 'calm' : voiceBuddyMood;
  /* in the session player the buddy breathes slowly with the session */
  const relaxBuddyMood: BuddyMood = relaxView === 'player' && voiceBuddyMood === 'idle' ? 'calm' : voiceBuddyMood;
  /* ---- screen inventory (drives the prototype + documents it) ---- */
  const inventory: {
    n: string;
    title: string;
    desc: string;
    go: () => void;
  }[] = [{
    n: '01',
    title: 'Dashboard',
    desc: 'Greeting, contextual recommendations, 8 feature cards, persistent chat dock',
    go: reset
  }, {
    n: '02',
    title: 'Conversation',
    desc: 'Voice + chat hybrid — query → semantic search → result card in chat',
    go: () => {
      reset();
      setChatOpen(true);
    }
  }, {
    n: '03',
    title: 'Weather detail',
    desc: 'Bottom modal — forecast, pollen meter, care-plan guidance',
    go: () => {
      reset();
      setModal('weather');
    }
  }, {
    n: '04',
    title: 'Article reader',
    desc: 'Full-screen modal — opens inside the Library stack',
    go: () => {
      reset();
      setLibraryStart('article');
      setLibraryOpen(true);
    }
  }, {
    n: '05',
    title: 'Tracking logger',
    desc: 'Bottom modal — 8-tracker picker → quick log → celebration, plus the two-week history view (inhaler chart + every log). "How\'s my tracking going?" in chat routes straight to history',
    go: () => {
      reset();
      openModal('tracking');
    }
  }, {
    n: '06',
    title: 'Health Library',
    desc: 'Full-screen modal — articles list → reader in one stack',
    go: () => {
      reset();
      setLibraryStart('list');
      setLibraryOpen(true);
    }
  }, {
    n: '07',
    title: 'Settings',
    desc: 'Full-screen modal — hub → sub-views in one stack',
    go: () => {
      reset();
      setSettingsOpen(true);
    }
  }, {
    n: '08',
    title: 'Journey',
    desc: 'Bottom modal — Duolingo-style map → lesson player → step complete. The Sunny avatar in the header toggles voice mode',
    go: () => {
      reset();
      openModal('journey');
    }
  }, {
    n: '09',
    title: 'Care Plans',
    desc: 'Bottom modal — plan picker → action plan zones / med schedule / emergency steps. "I\'m struggling to breathe" in chat routes straight to emergency',
    go: () => {
      reset();
      openModal('plans');
    }
  }, {
    n: '10',
    title: 'Relax & Sleep',
    desc: 'Bottom modal — session picker (Tonight + Last night) → calm player with soundscapes / sleep log → celebration. "I can\'t sleep" in chat goes straight to the wind-down player',
    go: () => {
      reset();
      openModal('relax');
    }
  }];
  return <div className="dhp-grain flex min-h-screen w-full bg-teal-deep font-sans" data-id="element-0">
      {/* ============ presentation panel ============ */}
      <aside className="relative z-10 hidden w-[400px] shrink-0 flex-col justify-between p-10 text-cream lg:flex" data-id="element-1">
        <div data-id="element-2">
          <div className="flex items-center gap-3" data-id="element-3">
            <HealthBuddy size={46} mood="idle" data-id="element-4" />
            <div data-id="element-5">
              <h1 className="font-display text-[26px] font-semibold leading-none" data-id="element-6">
                dhp.voice
              </h1>
              <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.2em] text-butter" data-id="element-7">
                App mockup · core screens
              </p>
            </div>
          </div>

          <p className="mt-6 max-w-[320px] text-[14px] leading-relaxed text-teal-soft" data-id="element-8">
            A conversational health companion. Touch-first navigation over
            curated content, with voice + chat + a health buddy as the interface
            layer on top.
          </p>

          {/* inventory */}
          <nav className="mt-8 flex flex-col gap-1" data-id="element-9">
            {inventory.map(item => <button key={item.n} onClick={item.go} className="group flex items-start gap-4 rounded-2xl p-3 text-left transition-colors hover:bg-[#FFFFFF14]" data-id="element-10">
                <span className="font-display text-[15px] font-medium text-butter" data-id="element-11">
                  {item.n}
                </span>
                <span data-id="element-12">
                  <span className="font-display block text-[16px] font-semibold leading-tight" data-id="element-13">
                    {item.title}
                  </span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-teal-soft opacity-80" data-id="element-14">
                    {item.desc}
                  </span>
                </span>
              </button>)}
          </nav>
        </div>

        {/* product principles — the two constraints every screen obeys */}
        <footer className="mt-8 border-t border-[#FFFFFF22] pt-6" data-id="element-15">
          <p className="text-[12px] leading-relaxed text-teal-soft" data-id="element-16">
            <span className="font-bold text-butter" data-id="element-17">
              Voice is an interface layer, not the product.
            </span>{' '}
            Every voice interaction also exists as text.
          </p>
          <p className="mt-3 text-[12px] leading-relaxed text-teal-soft" data-id="element-18">
            <span className="font-bold text-butter" data-id="element-19">
              The assistant finds curated content
            </span>{' '}
            — it never generates medical advice.
          </p>
        </footer>
      </aside>

      {/* ============ the app ============ */}
      <main className="relative z-10 flex flex-1 items-center justify-center overflow-hidden p-6" data-id="element-20">
        <div className="origin-center scale-[0.78] sm:scale-[0.85] xl:scale-[0.92] 2xl:scale-100" data-id="element-21">
          <PhoneFrame data-id="element-22">
            {/* current screen */}
            {screen === 'dashboard' && <Dashboard onFeature={handleFeature} onOpenModal={openModal} onSettings={() => setSettingsOpen(true)} onToggleChat={() => setChatOpen(o => !o)} data-id="element-23" />}
            {/* journey — full-screen, in-navigation flow (conditions list →
             map → lesson → complete). The × closes the whole stack back to
             the dashboard. */}
            <JourneyScreen open={journeyOpen} onClose={() => setJourneyOpen(false)} conditions={selectedConditions} data-id="element-24" />

            {/* library — full-screen modal flow (articles list → reader in
             one stack). The × closes back to the dashboard. */}
            <LibraryScreen open={libraryOpen} onClose={() => setLibraryOpen(false)} startAt={libraryStart} data-id="element-25" />

            {/* settings — full-screen modal flow (hub → sub-views in one
             stack). The × closes back to the dashboard. */}
            <SettingsScreen open={settingsOpen} onClose={() => setSettingsOpen(false)} data-id="element-26" />

            {/* nhs — full-screen modal flow */}
            <NhsScreen open={nhsOpen} onClose={() => setNhsOpen(false)} data-id="element-27" />

            {/* chat — only on the dashboard, hidden on other screens and
             while a full-screen slide-in (spaces / support / journey) is open */}
            {screen === 'dashboard' && !journeyOpen && !libraryOpen && !settingsOpen && !nhsOpen && <>
                  {/* ChatDock hidden — chat is now toggled via the Sunny
                   avatar in the header. Kept here intentionally for now.
                   <ChatDock
                   onOpen={() => setChatOpen(true)}
                   onMic={handleDockMic}
                   onSuggestion={handleDockSuggestion}
                   />
                   */}
                  <ChatSheet open={chatOpen} onClose={() => setChatOpen(false)} onOpenModal={openModalFromChat} pendingScript={pendingScript} onScriptConsumed={() => setPendingScript(null)} data-id="element-28" />
                </>}

            {/* bottom modals — the universal deep-navigation pattern */}
            <BottomModal open={modal === 'weather'} onClose={closeModal} data-id="element-29">
              <WeatherModal data-id="element-30" />
            </BottomModal>
            {/* Tracking — full-screen slide-up modal with a persistent × */}
            <FullScreenModal open={modal === 'tracking'} onClose={closeModal} data-id="element-31">
              <TrackingModal onDone={closeModal} startAt={trackingStart} data-id="element-32" />
            </FullScreenModal>
            <BottomModal open={modal === 'journey'} onClose={closeModal} maxHeight="94%" buddyMood={journeyBuddyMood} buddyActive={voiceMode} onBuddyTap={() => setVoiceMode(v => !v)} data-id="element-33">
              <JourneyModal onDone={closeModal} startAt={journeyStart} voiceMode={voiceMode} onVoiceActivity={setVoiceActivity} conditions={selectedConditions} data-id="element-34" />
            </BottomModal>
            {/* Plans — full-screen slide-up modal with a persistent × top-right.
             FullScreenModal auto-resets the inner view on each open. */}
            <FullScreenModal open={modal === 'plans'} onClose={closeModal} data-id="element-35">
              <CarePlansModal onDone={closeModal} onLogMed={handleLogMed} startAt={plansStart} allowBack={plansAllowBack} voiceMode={voiceMode} onVoiceActivity={setVoiceActivity} onViewChange={setPlansView} data-id="element-36" />
            </FullScreenModal>
            <BottomModal open={modal === 'relax'} onClose={closeModal} maxHeight="94%" buddyMood={relaxBuddyMood} buddyActive={voiceMode} onBuddyTap={() => setVoiceMode(v => !v)} data-id="element-37">
              <RelaxModal onDone={closeModal} startAt={relaxStart} voiceMode={voiceMode} onVoiceActivity={setVoiceActivity} onViewChange={setRelaxView} data-id="element-38" />
            </BottomModal>
          </PhoneFrame>
        </div>

        {/* mobile hint when the panel is hidden */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center lg:hidden" data-id="element-39">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-soft" data-id="element-40">
            dhp.voice · app mockup
          </p>
        </div>
      </main>
    </div>;
}